import { Plan } from '@/services/frandroid-api';
import { PLAN_TYPES, type PlanType } from '@/config/api';

export type HighlightType = 'coup-de-coeur' | 'le-pas-cher' | 'meilleur-ratio' | 'le-plus-complet' | 'le-plus-populaire';

export interface PlanWithHighlight extends Plan {
  highlight?: HighlightType;
}

export interface HighlightConfig {
  highlightIds?: string[];
  planType: PlanType;
}

export function computeHighlights(
  plans: Plan[],
  config: HighlightConfig
): PlanWithHighlight[] {
  if (plans.length === 0) return [];

  const { highlightIds = [], planType } = config;
  const isMobile = planType === PLAN_TYPES.MOBILE;
  const assignedIds = new Set<string>();
  const result: PlanWithHighlight[] = plans.map(p => ({ ...p }));

  // 1. Editorial picks (highest priority)
  for (const id of highlightIds) {
    const plan = result.find(p => p.id === id);
    if (plan && !assignedIds.has(plan.id)) {
      plan.highlight = 'coup-de-coeur';
      assignedIds.add(plan.id);
    }
  }

  // 2. Cheapest plan ("Le Pas Cher")
  const forCheapest = result.filter(p => !assignedIds.has(p.id));
  if (forCheapest.length > 0) {
    const cheapest = forCheapest.reduce((min, p) =>
      p.offer.price.price < min.offer.price.price ? p : min
    );
    const found = result.find(p => p.id === cheapest.id);
    if (found) {
      found.highlight = 'le-pas-cher';
      assignedIds.add(found.id);
    }
  }

  // 3. Best ratio (GB/price for mobile, speed/price for internet)
  const forRatio = result.filter(p => !assignedIds.has(p.id));
  if (forRatio.length > 0) {
    const bestRatio = forRatio.reduce((best, p) => {
      const getValue = (plan: Plan) => isMobile
        ? (plan.extra?.data?.data || 0)
        : (plan.extra?.internet_speed || 0);
      const ratio = getValue(p) / (p.offer.price.price || 1);
      const bestR = getValue(best) / (best.offer.price.price || 1);
      return ratio > bestR ? p : best;
    });
    const found = result.find(p => p.id === bestRatio.id);
    if (found) {
      found.highlight = 'meilleur-ratio';
      assignedIds.add(found.id);
    }
  }

  // 4. Most complete (most GB for mobile, best speed for internet)
  const forComplete = result.filter(p => !assignedIds.has(p.id));
  if (forComplete.length > 0) {
    const mostData = forComplete.reduce((max, p) => {
      const getValue = (plan: Plan) => isMobile
        ? (plan.extra?.data?.data || 0)
        : (plan.extra?.internet_speed || 0);
      return getValue(p) > getValue(max) ? p : max;
    });
    const found = result.find(p => p.id === mostData.id);
    if (found) {
      found.highlight = 'le-plus-complet';
      assignedIds.add(found.id);
    }
  }

  return result;
}

export const HIGHLIGHT_CONFIG: Record<HighlightType, { label: string; className: string }> = {
  'coup-de-coeur': { label: 'Le Coup de Coeur', className: 'highlight--coup-de-coeur' },
  'le-pas-cher': { label: 'Le Pas Cher', className: 'highlight--pas-cher' },
  'le-plus-complet': { label: 'Le Plus Complet', className: 'highlight--complet' },
  'meilleur-ratio': { label: 'Le Meilleur Rapport Go/Prix', className: 'highlight--ratio' },
  'le-plus-populaire': { label: 'Le Plus Populaire', className: 'highlight--populaire' },
};

export function getHighlightLabel(type: HighlightType, planType?: PlanType): string {
  if (type === 'meilleur-ratio') {
    return planType === PLAN_TYPES.INTERNET
      ? 'Le Meilleur Rapport Débit/Prix'
      : 'Le Meilleur Rapport Go/Prix';
  }
  return HIGHLIGHT_CONFIG[type].label;
}
